/**
 * advance-energy-regen.ts — per-tick ship energy recovery.
 *
 * Paired with advance-cloak.ts: cloak drains, this pass gives. Runs AFTER
 * cloak drain in advanceTick so a ship that auto-decloaked this tick (e.g.
 * from EMP or a jump) still regenerates on the same tick rather than
 * standing still for one frame.
 *
 * Skip conditions (no regen this tick):
 *   - Cloaked — cloak upkeep is consuming energy this tick; letting regen
 *     cancel it defeats the whole mechanic.
 *   - Mid-jump (systemId === null) — the ship is in hyperspace; reactor
 *     state is frozen until arrival, mirroring how drain is skipped while
 *     mid-jump.
 *   - EMP-disabled (disabledTicks > 0) — reactor is offline along with
 *     everything else that takes power (MECHANICS §2.2).
 *   - Template has null energyRegen or null energyCapacity — the chassis
 *     doesn't model energy at all (small auxiliaries, pure-hull designs).
 *     currentEnergy is left untouched.
 *
 * Regen amount and cap come straight off the template (ShipTemplate.energyRegen
 * and .energyCapacity). currentEnergy is clamped to capacity every tick — if a
 * ship hits cap it just sits there, no over-fill.
 */

import type { GameState } from '../state/types/index';

/** Restore currentEnergy by template.energyRegen, clamped to energyCapacity. */
export function advanceShipEnergyRegen(state: GameState): void {
  for (const ship of state.ships.values()) {
    if (ship.cloaked) continue;
    if (ship.systemId === null) continue; // mid-jump
    if (ship.disabledTicks > 0) continue; // EMP offline

    const empire = state.empires.get(ship.ownerEmpireId);
    if (!empire) continue;
    const tmpl = state.shipTemplates.get(empire.raceId + ':' + ship.chassis);
    if (!tmpl) continue;
    if (tmpl.energyRegen === null) continue;
    if (tmpl.energyCapacity === null) continue;

    const next = ship.currentEnergy + tmpl.energyRegen;
    ship.currentEnergy = next > tmpl.energyCapacity ? tmpl.energyCapacity : next;
  }
}
